import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useEditFocusStore } from "../stores/editFocusStore"

/**
 * URL-driven active task state.
 *
 * Task detail is controlled via `?tid=<taskId>` search parameter.
 * This matches v1's pattern (Next.js `router.query.tid`), ported to
 * react-router-dom `useSearchParams`.
 *
 * Edit focus is now handled by `editFocusStore` (one-shot Zustand signal)
 * instead of the old `?editTaskName=true` query param, which polluted
 * the URL and required explicit cleanup in many places.
 */
export function useActiveTask() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestEditFocus = useEditFocusStore((s) => s.requestEditFocus)

  const activeTaskId = searchParams.get("tid")
  // Legacy compat: still read from URL for any lingering links
  const isEditingTitle = searchParams.get("editTaskName") === "true"

  const openTask = useCallback(
    (id: string, editTitle = false) => {
      if (editTitle) {
        requestEditFocus(id)
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set("tid", id)
          next.delete("editTaskName") // clean up legacy param
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams, requestEditFocus],
  )

  const closeTask = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("tid")
        next.delete("editTaskName")
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  return {
    activeTaskId,
    isEditingTitle,
    openTask,
    closeTask,
  } as const
}
